import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';
import { GUEST_RESERVE_SERVICE_STEP } from 'constants/reservation';
import AprosButton from 'components/aprosButton';
import StepperGuest from 'components/stepperGuest/index';
import Proctitle from 'components/proctitle';
import Linked from 'components/linked/index';
import InputTitleLabelGuest from 'components/inputTitleLabelGuest';
import InputTextGuest from 'components/inputTextGuest/index';
import Checkbox from 'components/checkbox/index';
import { userInfoValidationSchema } from 'utils/guestReserveValidationSchemaUtil';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { useDispatch } from 'react-redux';
import { messageModalActions } from 'slices/messageModalSlice';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestUserInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!location.state?.carInfo || !location.state?.shopAndDateInfo) {
      const message = ERROR_MESSAGE_TRANSITION;
      dispatch(messageModalActions.showMessageModal({ message }));
      navigate(Path.GUEST_RESERVATION);
      return;
    }
  }, [location.state, navigate]);

  // 必須項目が全て入力されているか判定
  const isAllRequiredFilled = values => {
    return (
      values.lastName.trim() &&
      values.firstName.trim() &&
      values.lastNameKana.trim() &&
      values.firstNameKana.trim() &&
      values.phoneNumber.trim() &&
      values.email.trim() &&
      values.emailConfirm.trim()
    );
  };

  return (
    <Formik
      initialValues={{
        lastName: location.state?.userInfo?.lastName || '',
        firstName: location.state?.userInfo?.firstName || '',
        lastNameKana: location.state?.userInfo?.lastNameKana || '',
        firstNameKana: location.state?.userInfo?.firstNameKana || '',
        phoneNumber: location.state?.userInfo?.phoneNumber || '',
        email: location.state?.userInfo?.email || '',
        emailConfirm: location.state?.userInfo?.emailConfirm || '',
      }}
      validateOnChange={true}
      validateOnBlur={true}
      validationSchema={userInfoValidationSchema}
      onSubmit={values => {
        navigate(Path.GUEST_CONFIRM, {
          state: {
            ...location.state,
            previousStep: 2,
            userInfo: values,
          },
        });
      }}
    >
      {({ handleSubmit, isValid, values, setFieldValue, setFieldTouched }) => (
        <Form>
          <div className="mt-4 lg:px-20">
            <Helmet>
              <title>{Title.GUEST_RESERVATION}</title>
            </Helmet>
            <StepperGuest
              labels={GUEST_RESERVE_SERVICE_STEP}
              activeStep={2}
              fullWidth={true}
              className={{
                stepperClass: 'max-sm:mx-8 sm:mx-36',
                bottomClass: 'mx-0 h-full w-full text-left',
              }}
              stepPaths={[
                        Path.GUEST_SHOP_AND_DATE_SELECT,
                        Path.GUEST_CAR_INFO,
                      ]}
              navigate={navigate}
              locationState={location.state}
            />
            <div className="mt-12 sm:mt-24">
              <Proctitle
                useH1={true}
                text="STEP3 お客様情報の入力"
                className={{ textClass: 'whitespace-nowrap' }}
              />
            </div>
            <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex flex-row break-words sm:mt-12 sm:w-1/3 sm:min-w-[250px]">
                  <InputTitleLabelGuest title="お名前" required={true} />
                  <span className="text-xs mt-1 text-[#4C657C] w-[80px] break-words">
                    全角
                  </span>
                </div>
                <div className="flex flex-row items-end gap-2 sm:w-1/2 sm:gap-4 sm:min-w-[320px]">
                  <Field name="lastName">
                    {({ field, form }) => (
                      <InputTextGuest
                        formik={form}
                        fieldName={field.name}
                        value={field.value}
                        onChange={e => {
                          form.setFieldValue(field.name, e.target.value);
                          form.setFieldTouched(field.name, true, false);
                        }}
                        onBlur={field.onBlur}
                        placeholder="音場"
                        error={
                          form.errors[field.name] && form.touched[field.name]
                            ? form.errors[field.name]
                            : ''
                        }
                        touched={form.touched[field.name]}
                      />
                    )}
                  </Field>
                  <Field name="firstName">
                    {({ field, form }) => (
                      <InputTextGuest
                        formik={form}
                        fieldName={field.name}
                        value={field.value}
                        onChange={e => {
                          form.setFieldValue(field.name, e.target.value);
                          form.setFieldTouched(field.name, true, false);
                        }}
                        onBlur={field.onBlur}
                        placeholder="久須夫"
                        error={
                          form.errors[field.name] && form.touched[field.name]
                            ? form.errors[field.name]
                            : ''
                        }
                        touched={form.touched[field.name]}
                      />
                    )}
                  </Field>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex flex-row break-words sm:mt-12 sm:w-1/3 sm:min-w-[250px]">
                    <InputTitleLabelGuest title="フリガナ" required={true} />
                    <span className="text-xs mt-1 text-[#4C657C] w-[80px] break-words">
                      全角カナ
                    </span>
                  </div>
                  <div className="flex flex-row items-end gap-2 sm:w-1/2 sm:gap-4 sm:min-w-[320px]">
                    <Field name="lastNameKana">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="オトバ"
                          error={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                    <Field name="firstNameKana">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="クスオ"
                          error={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex flex-row break-words sm:mt-12 sm:w-1/3 sm:min-w-[250px]">
                    <InputTitleLabelGuest title="電話番号" required={true} />
                    <span className="text-xs mt-1 text-[#4C657C] break-words sm:min-w-[40px]">
                      ハイフン抜き半角数字
                    </span>
                  </div>
                  <div className="flex flex-row sm:w-[calc(50%/2-0.5rem)] sm:min-w-[150px]">
                    <Field name="phoneNumber">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="0000000000"
                          error={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex flex-row break-words sm:mt-12 sm:w-1/3 sm:min-w-[250px]">
                    <InputTitleLabelGuest title="メールアドレス" required={true} />
                  </div>
                  <div className="flex flex-row items-end gap-2 sm:w-1/2 sm:gap-4 sm:min-w-[320px]">
                    <Field name="email">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="info@autobacs.com"
                          error={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-2 sm:mb-2">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex flex-row break-words sm:mt-12 sm:w-1/3 sm:min-w-[250px]">
                    <InputTitleLabelGuest
                      title="メールアドレス(確認用)"
                      required={true}
                    />
                  </div>
                  <div className="flex flex-row items-end gap-2 sm:w-1/2 sm:gap-4 sm:min-w-[320px]">
                    <Field name="emailConfirm">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="info@autobacs.com"
                          error={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <AprosButton
                text="内容確認へ進む"
                comClassName="sub"
                onClick={isValid ? handleSubmit : undefined}
                className="mb-4 w-[260px] hover:text-navy"
                disabled={!isValid || !isAllRequiredFilled(values)}
              />
            </div>
            <div className="mt-8 mb-12 flex justify-center items-center text-center sm:mb-24">
              <Linked
                text="お車情報の入力へ戻る"
                onClick={() => navigate(Path.GUEST_CAR_INFO, { state: location.state })}
                isLine
                linkIcon="L"
                className="font-bold"
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default GuestUserInfo;
